import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DocumentTypeCard from './components/DocumentTypeCard';
import UploadModal from './components/UploadModal';
import VerificationProgress from './components/VerificationProgress';
import SecurityInfo from './components/SecurityInfo';
import DocumentViewer from './components/DocumentViewer';

const DocumentUpload = () => {
  const navigate = useNavigate();
  const [user] = useState({
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    role: "customer"
  });

  const [uploadModal, setUploadModal] = useState({ isOpen: false, documentType: null });
  const [viewerModal, setViewerModal] = useState({ isOpen: false, document: null });
  const [documents, setDocuments] = useState({
    id: {
      title: 'Identity Document',
      description: 'Government-issued photo ID',
      requirements: [
      'Clear, high-resolution image',
      'All corners visible',
      'Text must be readable',
      'No glare or shadows'],

      status: 'completed',
      uploadedFile: {
        name: 'passport_sarah_johnson.jpg',
        size: '2.4 MB',
        uploadDate: '15/11/2025',
        type: 'image',
        url: "https://images.unsplash.com/photo-1467069827872-e954fba222f9",
        documentType: 'Identity Document'
      }
    },
    address: {
      title: 'Proof of Address',
      description: 'Recent utility bill or bank statement',
      requirements: [
      'Document dated within 3 months',
      'Full name and address visible',
      'Official letterhead present',
      'No handwritten documents'],

      status: 'pending',
      uploadedFile: {
        name: 'utility_bill_november_2025.pdf',
        size: '1.8 MB',
        uploadDate: '16/11/2025',
        type: 'pdf',
        url: '#',
        documentType: 'Proof of Address'
      }
    },
    selfie: {
      title: 'Selfie Verification',
      description: 'Clear photo of yourself',
      requirements: [
      'Face clearly visible',
      'Good lighting conditions',
      'Look directly at camera',
      'No filters or editing'],

      status: 'rejected',
      uploadedFile: {
        name: 'selfie_verification.jpg',
        size: '3.1 MB',
        uploadDate: '14/11/2025',
        type: 'image',
        url: "https://images.unsplash.com/photo-1661724242952-7523bd91dc40",
        documentType: 'Selfie Verification',
        rejectionReason: 'Image quality is too low. Please ensure good lighting and camera focus when taking your selfie.'
      }
    }
  });

  const [overallStatus, setOverallStatus] = useState('pending');

  useEffect(() => {
    const statuses = Object.values(documents)?.map((doc) => doc?.status);
    if (statuses?.every((status) => status === 'completed')) {
      setOverallStatus('completed');
    } else if (statuses?.some((status) => status === 'rejected')) {
      setOverallStatus('rejected');
    } else {
      setOverallStatus('pending');
    }
  }, [documents]);

  const handleUploadClick = (documentType) => {
    setUploadModal({ isOpen: true, documentType });
  };

  const handleUpload = (documentType, file) => {
    const newFile = {
      name: file?.name,
      size: `${(file?.size / (1024 * 1024))?.toFixed(1)} MB`,
      uploadDate: new Date()?.toLocaleDateString('en-GB'),
      type: file?.type?.startsWith('image/') ? 'image' : 'pdf',
      url: file?.type?.startsWith('image/') ? URL.createObjectURL(file) : '#',
      documentType: documents?.[documentType]?.title
    };

    setDocuments((prev) => ({
      ...prev,
      [documentType]: {
        ...prev?.[documentType],
        status: 'pending',
        uploadedFile: newFile
      }
    }));

    // Simulate processing
    setTimeout(() => {
      setDocuments((prev) => ({
        ...prev,
        [documentType]: {
          ...prev?.[documentType],
          status: 'completed'
        }
      }));
    }, 3000);
  };

  const handleViewDocument = (documentType) => {
    const document = documents?.[documentType]?.uploadedFile;
    if (document) {
      setViewerModal({ isOpen: true, document });
    }
  };

  const handleLogout = () => {
    console.log('User logged out');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="hover:text-foreground transition-colors">

              Dashboard
            </button>
            <Icon name="ChevronRight" size={16} />
            <span className="text-foreground">Document Upload</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Document Verification</h1>
              <p className="text-lg text-muted-foreground mt-2">
                Upload your documents to complete identity verification
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/profile-management')}
              iconName="User"
              iconPosition="left"
              iconSize={16}>

              View Profile
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-1 gap-6">
              {Object.entries(documents)?.map(([key, doc]) =>
              <DocumentTypeCard
                key={key}
                type={key}
                title={doc?.title}
                description={doc?.description}
                requirements={doc?.requirements}
                status={doc?.status}
                uploadedFile={doc?.uploadedFile}
                onUpload={handleUploadClick}
                onView={handleViewDocument} />

              )}
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Important Guidelines</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <Icon name="Check" size={14} className="text-success flex-shrink-0 mt-0.5" />
                      <span>Ensure all documents are clear and readable</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Icon name="Check" size={14} className="text-success flex-shrink-0 mt-0.5" />
                      <span>Documents must be recent and valid</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Icon name="Check" size={14} className="text-success flex-shrink-0 mt-0.5" />
                      <span>Supported formats: JPG, PNG, PDF (max 10MB)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Icon name="Check" size={14} className="text-success flex-shrink-0 mt-0.5" />
                      <span>Processing typically takes 1-2 business days</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <VerificationProgress
              documents={documents}
              overallStatus={overallStatus} />

            <SecurityInfo />
          </div>
        </div>

        {overallStatus === 'completed' &&
        <div className="mt-8 bg-success/10 border border-success/20 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} color="white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-success">Verification Complete!</h3>
                  <p className="text-sm text-success/80">
                    Your identity has been verified. You can now access all features.
                  </p>
                </div>
              </div>
              <Button
              variant="default"
              onClick={() => navigate('/send-money-flow')}
              iconName="Send"
              iconPosition="left"
              iconSize={16}>

                Start Sending Money
              </Button>
            </div>
          </div>
        }
      </div>
      <UploadModal
        isOpen={uploadModal?.isOpen}
        onClose={() => setUploadModal({ isOpen: false, documentType: null })}
        documentType={uploadModal?.documentType}
        onUpload={handleUpload} />

      <DocumentViewer
        isOpen={viewerModal?.isOpen}
        onClose={() => setViewerModal({ isOpen: false, document: null })}
        document={viewerModal?.document} />

    </div>);

};

export default DocumentUpload;